'use client'

import ControllerIcon from '@/components/icons/controller-icon'
import FlameIcon from '@/components/icons/flame'
import HomeIcon from '@/components/icons/home'
import MagnifyingGlassIcon from '@/components/icons/magnifying-glass'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  ChevronRight,
  SearchIcon,
  SearchXIcon,
  XIcon,
  ZapIcon,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { memo, useMemo, useState } from 'react'

const GameCard = memo(({ game }) => {
  return (
    <Link href={`/sim-racing-games/${game.name}`} className="group">
      <div className="rounded-sm border-2 border-neutral-blue-600 transition-all duration-300 ease-in-out group-hover:border-neutral-blue-500">
        <Image
          alt={game.name}
          height={600}
          width={900}
          className="aspect-2/3 rounded-[3px] object-cover object-center"
          src={game.cardImgUrl || undefined}
        />
      </div>
      <p className="mt-3 text-p-2 font-semibold underline decoration-transparent underline-offset-2 transition-all duration-300 ease-in-out group-hover:decoration-white desktop-xs:text-p-3">
        {game.longname}
      </p>
    </Link>
  )
})

const GameSection = memo(
  ({ games, title, Icon, iconBgColor }) =>
    games?.length > 0 && (
      <>
        <div className="mt-[52px] flex flex-col gap-3 first:mt-0 desktop-xs:mt-[60px] desktop-xs:gap-6">
          <div className={cn('divide', iconBgColor)} />
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'grid size-10 items-center justify-center rounded-sm desktop-xs:size-12',
                iconBgColor,
              )}
            >
              <Icon className="size-6 stroke-white stroke-2" />
            </div>
            <p className="text-h-5-sm font-semibold desktop-xs:text-h-5">
              {title}
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 tablet:grid-cols-3 desktop-xs:mt-6 desktop-xs:grid-cols-6 desktop-xs:gap-8">
          {games.map((game) => (
            <GameCard key={game.name} game={game} />
          ))}
        </div>
      </>
    ),
)

const EmptySearchState = memo(({ searchText, onClearSearch }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-neutral-blue-700">
      <SearchXIcon className="size-8 stroke-neutral-blue-300 stroke-2" />
    </div>

    <h3 className="mb-2 text-h-4-sm font-semibold text-shades-0 desktop-xs:text-h-4">
      No games found
    </h3>

    <p className="mb-6 max-w-md text-p-2 text-neutral-blue-300">
      We couldn't find any games matching "{searchText}". Try adjusting your
      search or browse all available games below.
    </p>

    <button
      onClick={onClearSearch}
      className="inline-flex items-center gap-2 rounded-md bg-neutral-blue-600 px-4 py-2 text-p-2 font-medium text-shades-0 transition-colors hover:bg-neutral-blue-500"
    >
      <XIcon className="size-4" />
      Clear search
    </button>
  </div>
))

export default function AllGames({ games, popularGames, recentlyAddedGames }) {
  const [searchText, setSearchText] = useState('')

  const filteredGames = useMemo(
    () =>
      games?.filter((game) =>
        [game.name, game.longname, game.nicename].some((text) =>
          text?.toLowerCase().includes(searchText.toLowerCase()),
        ),
      ),
    [games, searchText],
  )

  return (
    <div className="max-w-(--breakpoint-desktop-lg) flex w-full flex-col">
      <div className="screen-grid bg-linear-to-b grid w-full rounded-b-xl from-neutral-blue-900 to-[#08111B] pb-[34px] desktop-xs:pb-20">
        <div className="col-span-full grid desktop-xs:col-span-5 desktop-xs:col-start-2">
          {/* Nav */}
          <div className="col-span-full flex h-[53px] items-center gap-2 desktop-xs:h-[67px]">
            <Link href="/" className="max-w-1/2 group flex items-center gap-2">
              <HomeIcon className="size-4 min-w-4 stroke-neutral-blue-400 stroke-2 transition-all duration-300 ease-in-out group-hover:stroke-neutral-blue-200" />

              <p className="truncate-1 break-all text-p-1 font-bold text-neutral-blue-400 transition-all duration-300 ease-in-out group-hover:text-neutral-blue-200 desktop-xs:font-semibold">
                Home
              </p>
            </Link>

            <div className="max-w-1/2 flex items-center gap-2">
              <ChevronRight className="size-4 min-w-4 stroke-neutral-blue-400 stroke-[2.5px]" />

              <Link href="/sim-racing-games">
                <p className="truncate-1 break-all text-p-1 font-bold text-shades-0 transition-all duration-300 ease-in-out hover:text-shades-0/80 desktop-xs:font-semibold">
                  Games
                </p>
              </Link>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-3 desktop-xs:mt-5 desktop-xs:gap-5">
            <p className="text-h-2-sm font-bold">
              <span className="gf-gradient-text">Choose</span> your game
            </p>

            <p className="text-p-2 text-neutral-blue-200">
              Discover thousands of communities, racing events, liveries and
              broadcasters from over 40 different games supported on Grid
              Finder.
            </p>
          </div>
        </div>

        <div className="relative col-span-full mt-5 flex h-[46px] desktop-xs:col-span-3 desktop-xs:col-start-9 desktop-xs:self-end">
          <div className="pointer-events-none absolute ml-6 flex h-full items-center justify-center">
            <MagnifyingGlassIcon className="size-4 stroke-white stroke-2" />
          </div>
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="bg-neutral-blue-700! pl-12 text-p-2"
            placeholder="Search Games"
          />
        </div>
      </div>

      <div className="screen-grid mb-[126px]">
        <div className="col-span-full mt-6 flex flex-col desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:mt-[52px]">
          {searchText !== '' ? (
            filteredGames?.length > 0 ? (
              <GameSection
                games={filteredGames}
                title="Searched games"
                Icon={SearchIcon}
                iconBgColor="bg-[#484BD0]"
              />
            ) : (
              <EmptySearchState
                searchText={searchText}
                onClearSearch={() => setSearchText('')}
              />
            )
          ) : (
            <>
              <GameSection
                games={popularGames}
                title="Popular Games"
                Icon={FlameIcon}
                iconBgColor="bg-error-500"
              />
              <GameSection
                games={recentlyAddedGames}
                title="Recently Added"
                Icon={ZapIcon}
                iconBgColor="bg-[#484BD0]"
              />
              <GameSection
                games={games}
                title="All Games"
                Icon={ControllerIcon}
                iconBgColor="bg-warning-500"
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
